import React, { useCallback, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import Button from '@mui/material/Button'
import ClearIcon from '@mui/icons-material/Clear'
import DateRangePicker from '../../../components/DateRangePicker'
import useAxios from '../../../hooks/useAxios'
import { PaginationResponseInterface } from '../../../types/pagination'
import { IProduct } from '../../../types/product'
import { ICategory } from '../../../types/category'
import { IDistributionChannel } from '../../../types/distribution-channel'
import SelectSearchMultiple, {
  IOptionSelect,
} from '../../../components/SelectSearchMultiple'
import ExportExcel from '../../../components/Export'
import { AllowShippingProviderLists } from '../../../utils/provider'
import { useUser } from '../../../hooks/useUser'
import { ImageLinks, LogoTypeProvider } from '../../../constants/image-link'
import ProductGroupSelectSearch from '../../../components/SelectSearch/ProductGroupSelectSearch'
import ProductSelectSearch from '../../../components/SelectSearch/ProductSelectSearch'
import CategorySelectSearch from '../../../components/SelectSearch/CategorySelectSearch'
import ChannelSelectSearch from '../../../components/SelectSearch/ChannelSelectSearch'

export interface IFilterMapped {
  startDate?: string
  endDate?: string
  // provider?: string;
  categoryObjectIds?: string[]
  distributionObjectIds?: string[]
  distributionChannels?: string[]
  productObjectIds?: string[]
  productGroups?: string[]
  products?: string[]
  categories?: string[]
  shippingProvider?: string | any
  trackingStatus?: string
  page?: number
  perPage?: number
  isTelesale?: string
  dateType?: string
}

interface FilterGroupProps {
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  onSearch: (filter: IFilterMapped) => void
  showSippingProvider?: boolean
  showCategory?: boolean
  showDistributionChannel?: boolean
  showProduct?: boolean
  showDownload?: boolean
  showUser?: boolean
  onDownload?: (date: Dayjs) => void
}

const SendItemsReportFilter = (props: FilterGroupProps) => {
  const { merchant } = useUser()
  const {
    filter,
    onFilterChange,
    onSearch,
    showSippingProvider = true,
    showCategory = true,
    showDistributionChannel = true,
    showProduct = true,
    showDownload = true,
    onDownload,
  } = props
  const {
    startDate,
    endDate,
    productObjectIds,
    distributionObjectIds,
    categoryObjectIds,
  } = filter
  const [searchProduct, setSearchProduct] = useState<string>('')
  const [searchCategory, setSearchCategory] = useState<string>('')
  const [searchDistributionChannel, setSearchDistributionChannel] =
    useState<string>('')
  const [resetAutoComplete, setResetAutoComplete] = useState<string>('')

  const productsList = useAxios<PaginationResponseInterface<IProduct>>({
    method: 'GET',
    url: '/product',
    params: {
      page: 1,
      perPage: 5,
      search: searchProduct,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchProduct = async (value: string) => {
    setSearchProduct(value)
    await productsList.mutate()
  }

  const categoriesList = useAxios<PaginationResponseInterface<ICategory>>({
    method: 'GET',
    url: '/category',
    params: {
      page: 1,
      perPage: 5,
      search: searchCategory,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchCategory = async (value: string) => {
    setSearchCategory(value)
    await categoriesList.mutate()
  }

  const distributionChannelsList = useAxios<
    PaginationResponseInterface<IDistributionChannel>
  >({
    method: 'GET',
    url: '/distribution-channels',
    params: {
      page: 1,
      perPage: 5,
      search: searchDistributionChannel,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchDistributionChannel = async (value: string) => {
    setSearchDistributionChannel(value)
    try {
      await distributionChannelsList.mutate()
    } catch (error) {}
  }

  const handleFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      onFilterChange({ ...filter, ...newFilter })
    },
    [filter, onFilterChange],
  )

  return (
    <Grid container spacing={2} mb={2}>
      <Grid item xs={12} lg={6}>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={(startDate, endDate) => {
            onFilterChange({ ...filter, startDate, endDate })
          }}
        />
      </Grid>
      {showProduct && (
        <>
          <Grid item xs={12} lg={3}>
            <ProductSelectSearch
              products={productObjectIds}
              handleFilterChange={handleFilterChange}
              onSelect={(e) => {
                const selectedProductIds = e?.map(
                  (product: any) => product?.value?.objectId,
                )
                const selectedProducts = e?.map(
                  (product: any) => product?.value?.name,
                )
                handleFilterChange({
                  productObjectIds: selectedProductIds,
                  products: selectedProducts,
                })
              }}
            />
          </Grid>
          <Grid item xs={12} lg={3}>
            <ProductGroupSelectSearch
              resetAutoComplete={resetAutoComplete}
              productGroups={filter.productGroups}
              onSelect={(e) => {
                const selectedGroupIds = e?.map((group) => group.value.objectId)
                handleFilterChange({ productGroups: selectedGroupIds })
              }}
            />
          </Grid>
        </>
      )}
      {showCategory && (
        <Grid item xs={12} lg={4}>
          <CategorySelectSearch
            resetAutoComplete={resetAutoComplete}
            categories={categoryObjectIds}
            onSelect={(e) => {
              const selectedCategoryIds = e?.map(
                (category: any) => category?.value?.objectId,
              )
              const selectedCategories = e?.map(
                (category: any) => category?.value?.name,
              )
              onFilterChange({
                ...filter,
                categoryObjectIds: selectedCategoryIds,
                categories: selectedCategories,
              })
            }}
          />
        </Grid>
      )}
      {showDistributionChannel && (
        <Grid item xs={12} lg={4}>
          <ChannelSelectSearch
            resetAutoComplete={resetAutoComplete}
            label={'เลือกช่องทางการขาย'}
            onSelect={(e) => {
              const selectedChannelIds = e?.map(
                (channel: any) => channel?.value?.objectId,
              )
              const selectedChannels = e?.map(
                (channel: any) => channel?.value?.objectId,
              )
              onFilterChange({
                ...filter,
                distributionObjectIds: selectedChannelIds,
                distributionChannels: selectedChannels,
              })
            }}
          />
        </Grid>
      )}
      {showSippingProvider && (
        <Grid item xs={12} lg={4}>
          <FormControl variant="filled" fullWidth>
            <InputLabel id="type-label">เลือกขนส่ง</InputLabel>
            <Select
              id="shippingProvider"
              name="shippingProvider"
              labelId="shippingProvider-label"
              fullWidth
              value={filter.shippingProvider}
              onChange={(e) => {
                onFilterChange({
                  ...filter,
                  shippingProvider: e.target.value || '',
                })
              }}
            >
              <MenuItem value="" selected disabled={true}>
                <em>เลือกข่นส่ง</em>
              </MenuItem>
              <MenuItem value="all" key="all">
                ทั้งหมด
              </MenuItem>
              {AllowShippingProviderLists(merchant).map(
                (item, index: number) => (
                  <MenuItem value={item.value} key={index}>
                    {item.value !== 'all' && (
                      <img
                        style={{ marginRight: 5 }}
                        src={
                          ImageLinks[
                            `LOGO_${item.value.toUpperCase()}` as LogoTypeProvider
                          ]
                        }
                        alt="smallLogoSocial"
                        height={20}
                      />
                    )}
                    {item.label}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </Grid>
      )}
      <Grid item xs={12} lg={showDownload ? 2 : 4}>
        <Button
          color="primary"
          size="large"
          fullWidth
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => onSearch(filter)}
          sx={{ height: '100%' }}
        >
          ค้นหา
        </Button>
      </Grid>
      <Grid item xs={4} lg={2}>
        <Button
          color="inherit"
          size="large"
          fullWidth
          variant="contained"
          startIcon={<ClearIcon />}
          onClick={() => {
            onFilterChange({
              categoryObjectIds: undefined,
              distributionObjectIds: undefined,
              productObjectIds: undefined,
              productGroups: undefined,
              shippingProvider: 'all',
              trackingStatus: 'tracking',
              startDate: dayjs().subtract(30, 'days').format(),
              endDate: dayjs().format(),
            })
            onSearch({
              categoryObjectIds: undefined,
              distributionObjectIds: undefined,
              productObjectIds: undefined,
              productGroups: undefined,
              shippingProvider: 'all',
              trackingStatus: 'tracking',
              startDate: dayjs().subtract(30, 'days').format(),
              endDate: dayjs().format(),
            })
            setResetAutoComplete(new Date().getTime().toString())
          }}
          sx={{ height: '100%' }}
        >
          รีเซ็ต
        </Button>
      </Grid>
      {showDownload && (
        <Grid item xs={12} lg={2}>
          <ExportExcel onDownload={onDownload} filterDate={false} />
        </Grid>
      )}
    </Grid>
  )
}

export default SendItemsReportFilter
